import toast from "react-hot-toast";
export const DIALOG_TOASTER_ID = "dialog-service-toaster";
export type DialogTone = "default" | "warning" | "error" | "destructive";
type AlertKind = "info" | "success" | "warning" | "error";
export type DialogMessageInput = unknown;
export type DialogConfirmInput = string | DialogConfirmOptions;
export type DialogOptionsInput = string | DialogOptions;
export interface DialogOptions {
    title?: string;
    description?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    tone?: DialogTone;
    destructive?: boolean;
}
export interface DialogConfirmOptions extends DialogOptions {
}
export interface HandleAlertOptions {
    force?: AlertKind;
}
export interface DialogBridgeHandlers {
    confirm: (options: DialogConfirmOptions) => Promise<boolean>;
    warning: (options: DialogOptions) => Promise<void>;
    error: (options: DialogOptions) => Promise<void>;
}
const ERROR_PATTERN = /실패|오류|error|failed|wrong|불안정|요청실패/i;
const WARNING_PATTERN = /주의|유의|확인|선택|입력|필수|없습니다|찾을 수 없습니다|권한|만료|로그인/i;
const SUCCESS_PATTERN = /완료|성공|등록|수정|저장|추가|post ok|update ok|Delete ok/i;
const DESTRUCTIVE_PATTERN = /삭제|환불|취소/i;
let bridgeHandlers: DialogBridgeHandlers | null = null;
function isBrowser(): boolean {
    return typeof window !== "undefined";
}
function fallbackAlert(message: string): void {
  if (!message) return;
  if (isBrowser() && typeof window.alert === "function") {
    window.alert(message);
  }
}

function fallbackConfirm(message: string): boolean {
  if (!message) return true;
  if (isBrowser() && typeof window.confirm === "function") {
    return window.confirm(message);
  }
  return true;
}
export function registerDialogBridge(nextHandlers: DialogBridgeHandlers | null): void {
    bridgeHandlers = nextHandlers;
}
export function normalizeMessage(input: unknown): string {
    if (input === null || input === undefined)
        return "";
    if (typeof input === "string")
        return input;
    if (typeof input === "number" || typeof input === "boolean")
        return String(input);
    if (input instanceof Error)
        return input.message || input.name || "오류가 발생했습니다.";
    if (typeof input === "object") {
        const anyInput = input as Record<string, unknown>;
        if (typeof anyInput.message === "string")
            return anyInput.message;
        if (typeof anyInput.error === "string")
            return anyInput.error;
        if (typeof anyInput.detail === "string")
            return anyInput.detail;
        if (Array.isArray(anyInput.non_field_errors) && anyInput.non_field_errors.length > 0) {
            return anyInput.non_field_errors.map((v) => String(v)).join(", ");
        }
        try {
            return JSON.stringify(anyInput);
        }
        catch (_error) {
            return String(anyInput);
        }
    }
    return String(input);
}
function toDialogOptions(input: DialogOptionsInput, tone: DialogTone): DialogOptions {
    if (typeof input === "string") {
        return {
            description: input,
            tone,
            destructive: DESTRUCTIVE_PATTERN.test(input),
        };
    }
    const normalizedDescription = input.description ? normalizeMessage(input.description) : "";
    const normalizedTitle = input.title ? normalizeMessage(input.title) : "";
    const resolvedTone = input.tone ?? tone;
    const destructive = typeof input.destructive === "boolean"
        ? input.destructive
        : DESTRUCTIVE_PATTERN.test(normalizedTitle || normalizedDescription);
    return {
        ...input,
        title: normalizedTitle,
        description: normalizedDescription,
        tone: resolvedTone,
        destructive,
    };
}
function toConfirmOptions(input: DialogConfirmInput): DialogConfirmOptions {
    const options = toDialogOptions(input, "warning");
    return {
        ...options,
        tone: options.destructive ? "destructive" : "warning",
    };
}
export async function confirm(input: DialogConfirmInput): Promise<boolean> {
    const options = toConfirmOptions(input);
    if (bridgeHandlers?.confirm) {
        return bridgeHandlers.confirm(options);
    }
    return fallbackConfirm(options.description || options.title || "확인하시겠습니까?");
}
export async function warning(input: DialogOptionsInput): Promise<void> {
    const options = toDialogOptions(input, "warning");
    if (bridgeHandlers?.warning) {
        await bridgeHandlers.warning(options);
        return;
    }
    fallbackAlert(options.description || options.title || "확인해주세요.");
}
export async function error(input: DialogOptionsInput): Promise<void> {
    const options = toDialogOptions(input, "error");
    if (bridgeHandlers?.error) {
        await bridgeHandlers.error(options);
        return;
    }
    fallbackAlert(options.description || options.title || "오류가 발생했습니다.");
}
function classifyAlertKind(message: string, rawInput: unknown, forcedKind?: AlertKind): AlertKind {
    if (forcedKind)
        return forcedKind;
    if (rawInput instanceof Error)
        return "error";
    if (rawInput && typeof rawInput === "object") {
        const anyInput = rawInput as Record<string, unknown>;
        if (anyInput.isAxiosError || anyInput.stack || anyInput.response || anyInput.request)
            return "error";
    }
    if (ERROR_PATTERN.test(message))
        return "error";
    if (SUCCESS_PATTERN.test(message))
        return "success";
    if (WARNING_PATTERN.test(message))
        return "warning";
    return "warning";
}
export async function handleAlert(input: DialogMessageInput, options?: HandleAlertOptions): Promise<void> {
    const message = normalizeMessage(input);
    const kind = classifyAlertKind(message, input, options?.force);
    if (kind === "success") {
        notifySuccess(message);
        return;
    }
    if (kind === "info") {
        notifyInfo(message);
        return;
    }
    if (kind === "error") {
        await error({ description: message, tone: "error" });
        return;
    }
    await warning({
        description: message,
        tone: DESTRUCTIVE_PATTERN.test(message) ? "destructive" : "warning",
        destructive: DESTRUCTIVE_PATTERN.test(message),
    });
}
export function notifyInfo(input: DialogMessageInput): void {
    const message = normalizeMessage(input);
    if (!message)
        return;
    toast(message, { toasterId: DIALOG_TOASTER_ID });
}
export function notifySuccess(input: DialogMessageInput): void {
    const message = normalizeMessage(input);
    if (!message)
        return;
    toast.success(message, { toasterId: DIALOG_TOASTER_ID });
}
export const dialogService = {
    confirm,
    warning,
    error,
    handleAlert,
    notifyInfo,
    notifySuccess,
    normalizeMessage,
};
